import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { CognitoUser } from "amazon-cognito-identity-js";
import { isEqual, pick } from "lodash-es";
import Login from "@/views/Login.vue";
import pinia, { useAuth, useLayout, useLocation, useMap } from "@/services";
import dot from "dot-object";

Vue.use(VueRouter);
const watcher = Vue.observable({
  isLoggedIn: false,
  isInstanceReady: false,
  once: [] as { prop: string; handler: (...args: any) => any }[],
});

const watchedProps = ["isLoggedIn", "isInstanceReady"];

setTimeout(() => {
  new Vue({
    computed: {
      isLoggedIn() {
        return watcher.isLoggedIn;
      },
      isInstanceReady() {
        return watcher.isInstanceReady;
      },
    },
    watch: {
      ...Object.assign(
        {},
        ...watchedProps.map((prop) => ({
          [prop]: {
            immediate: true,
            handler(val: boolean) {
              watcher.once
                .filter((w) => val && w.prop === prop)
                .forEach((w) =>
                  typeof w.handler === "function" ? w.handler() : undefined
                );
              watcher.once = watcher.once.filter(
                (w) =>
                  !(val && w.prop === prop) && typeof w.handler === "function"
              );
            },
          },
        }))
      ),
    },
  });
  useAuth(pinia).$subscribe(() => {
    Object.assign(
      watcher,
      pick(useAuth(pinia), ["isLoggedIn", "isInstanceReady"])
    );
  });
});

export const getUrlQueryParam = (key: string, $router = router) =>
  (dot.object($router?.currentRoute?.query) || {})[key];

const getAddress = (ls = useLocation(pinia)) => {
  return ls.location?.urlParams || undefined;
};

export const updateUrlQueryParams = (
  prop: string,
  update: any,
  replace = false,
  $router = router
): Promise<any> => {
  const value = dot.object($router.currentRoute?.query || {})[prop];
  const mapPosition = useMap(pinia).mapPosition;
  if (!isEqual(value, update)) {
    const { isNavigationFailure, NavigationFailureType } = VueRouter;
    dot.keepArray = true;
    return $router
      .replace({
        query: dot.dot({
          ...$router.currentRoute?.query,
          address: cleanrUrlObject(getAddress()),
          mapPosition,
          [prop]: {
            ...(replace ? {} : value || {}),
            ...cleanrUrlObject(update),
          },
        }),
      })
      ?.catch((failure) => {
        if (
          !isNavigationFailure(failure, NavigationFailureType.cancelled) &&
          !isNavigationFailure(failure, NavigationFailureType.duplicated)
        ) {
          throw failure;
        }
      });
  }
  return Promise.resolve();
};

function cleanrUrlObject(obj: any) {
  if (!obj) return undefined;

  const updatedObj = { ...obj };
  Object.keys(updatedObj)
    .filter((x) => x.includes("_"))
    .forEach((redundantProp) => delete updatedObj[redundantProp]);
  if (updatedObj?.geometry) {
    updatedObj.geometry = encodeURI(JSON.stringify(updatedObj.geometry));
  }

  return updatedObj;
}
const requirePermission = async (to: any, from: any, next: any) => {
  if (watcher.isInstanceReady) {
    const hasPermission = await useAuth(pinia).hasPermission(
      to.meta.permission
    );
    next(hasPermission || !watcher.isLoggedIn ? undefined : { path: "/404" });
  } else {
    watcher.once.push({
      prop: "isInstanceReady",
      handler: () => requirePermission(to, from, next),
    });
  }
};

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "Home",
    component: () => import(/* webpackChunkName: "main" */ "../views/Home.vue"),
  },
  {
    path: "/login",
    name: "Login",
    props: true,
    component: Login,
  },
  {
    path: "/users",
    name: "Users",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/Users.vue"),
    beforeEnter: requirePermission,
    meta: {
      permission: ["GammaAdmin", "Admin"],
    },
  },
  {
    path: "/organisations",
    name: "Organisations",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/Organisations.vue"),
    beforeEnter: requirePermission,
    meta: {
      permission: ["GammaAdmin"],
    },
  },
  {
    path: "/reports",
    name: "Reports",
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/Reports.vue"),
    beforeEnter: requirePermission,
    meta: {
      permission: ["GammaAdmin"],
    },
  },
  {
    path: "/404",
    name: "Four-oh-four",
    component: () => import(/* webpackChunkName: "main" */ "../views/404.vue"),
  },
];

const router = new VueRouter({
  routes,
  base: process.env.VUE_APP_STAGE,
  mode: "history",
});

const checkAuth = (setPageLoading = true): Promise<CognitoUser | any> => {
  return new Promise((resolve) => {
    if (watcher.isInstanceReady) {
      if (setPageLoading) {
        useLayout(pinia).isPageLoading = true;
      }
      useAuth()
        .currentUserInfo()
        .catch(() => resolve(false))
        .then(() => {
          resolve(useAuth(pinia).isLoggedIn || false);
        });
    } else {
      watcher.once.push({
        prop: "isInstanceReady",
        handler() {
          resolve(checkAuth(setPageLoading));
        },
      });
    }
  });
};

router.beforeResolve((to, from, next) => {
  checkAuth(to.path !== from.path).then((isAuthenticated: boolean) =>
    next(
      to.path === "/login"
        ? isAuthenticated
          ? { name: "Home" }
          : undefined
        : isAuthenticated
        ? to.name
          ? undefined
          : { name: "Home" }
        : {
            name: "Login",
            params: {
              to: JSON.stringify(pick(to, ["params", "path", "name", "query"])),
            },
          }
    )
  );
});

router.afterEach((to) => {
  useLayout(pinia).$page = to.path.substring(1);
  useLayout(pinia).isPageLoading = false;
});

export default router;
